import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

/**
 * Monkey-patch defensivo a `HTMLMediaElement.prototype.play`.
 *
 * Por qué: la librería `html5-qrcode` (lector QR) crea un `<video>` y llama
 * `videoEl.play()` internamente sin un `.catch()`. Cuando React StrictMode
 * o el cierre rápido del modal quitan ese `<video>` del DOM antes de que la
 * promesa resuelva, el navegador rechaza con
 * `AbortError: The play() request was interrupted because the media was
 * removed from the document` y dispara `unhandledrejection` (que CRA
 * react-error-overlay muestra como overlay rojo).
 *
 * No es un bug funcional — el lector se recupera solo. Pero el overlay
 * confunde al usuario.
 *
 * Solución: envolvemos `play()` para que adjuntemos un handler silencioso
 * **al mismo promise origen**. Eso marca la rejection como manejada desde
 * el spec de Promises, sin esconder otros errores: cualquier rechazo
 * distinto a `AbortError` se re-lanza al microtask siguiente para que
 * siga siendo visible.
 *
 * No tocamos play() en otros casos (la app no tiene `<video>` propio
 * fuera del lector QR).
 */
(() => {
    if (typeof HTMLMediaElement === "undefined") return;
    const proto = HTMLMediaElement.prototype;
    if (proto.__playPatchedForQrAbort) return;
    const origPlay = proto.play;
    proto.play = function patchedPlay(...args) {
        const result = origPlay.apply(this, args);
        if (result && typeof result.then === "function") {
            result.then(undefined, (err) => {
                if (err && err.name === "AbortError") {
                    /* benigno: el media fue removido antes de que play() resolviera */
                    return;
                }
                /* re-lanzamos en otro microtask para que conserve visibilidad */
                setTimeout(() => {
                    throw err;
                }, 0);
            });
        }
        return result;
    };
    proto.__playPatchedForQrAbort = true;
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
